import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWishlist } from '../hooks/useWishlist';
import { useCart } from '../context/CartContext';
import { productsAPI } from '../utils/api';
import { FiTrash2, FiShoppingCart, FiShare2, FiHeart } from 'react-icons/fi';
import { getProductImageUrl, handleImageError } from '../utils/imageUtils';
import toast from 'react-hot-toast';
import './Wishlist.css';

const Wishlist = () => {
  const { wishlist, removeFromWishlist, clearWishlist } = useWishlist();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWishlistProducts();
  }, [wishlist]);

  const loadWishlistProducts = async () => {
    if (wishlist.length === 0) {
      setProducts([]);
      setLoading(false);
      return;
    }

    try {
      const promises = wishlist.map(id => productsAPI.getById(id));
      const responses = await Promise.all(promises);
      const loadedProducts = responses.map(res => res.data.product);
      setProducts(loadedProducts);
    } catch (error) {
      console.error('Error loading wishlist products:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (productId) => {
    removeFromWishlist(productId);
    toast.success('Removed from wishlist');
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    toast.success('Added to cart');
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success('Wishlist link copied!');
  };

  if (loading) {
    return (
      <div className="wishlist-page container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="wishlist-page container">
        <div className="empty-wishlist">
          <FiHeart size={60} />
          <h2>Your wishlist is empty</h2>
          <p>Save items you love for later</p>
          <Link to="/products" className="btn btn-primary">
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-page container">
      <div className="wishlist-header">
        <div>
          <h1>My Wishlist</h1>
          <p>{products.length} {products.length === 1 ? 'item' : 'items'}</p>
        </div>
        <div className="wishlist-actions">
          <button className="btn btn-outline" onClick={handleShare}>
            <FiShare2 /> Share
          </button>
          <button className="btn btn-outline" onClick={clearWishlist}>
            <FiTrash2 /> Clear All
          </button>
        </div>
      </div>

      <div className="wishlist-grid">
        {products.map(product => {
          const imageUrl = getProductImageUrl(product);

          return (
            <div key={product._id} className="wishlist-item">
              <Link to={`/products/${product._id}`}>
                <div className="wishlist-item-image">
                  <img 
                    src={imageUrl} 
                    alt={product.name}
                    onError={(e) => handleImageError(e)}
                  />
                </div>
              </Link>

              <div className="wishlist-item-info">
                <Link to={`/products/${product._id}`}>
                  <h3>{product.name}</h3>
                </Link>

                <div className="wishlist-item-price">
                  <span className="current-price">৳{product.price.toLocaleString()}</span>
                  {product.comparePrice && product.comparePrice > product.price && (
                    <>
                      <span className="compare-price">৳{product.comparePrice.toLocaleString()}</span>
                      <span className="discount-badge">
                        {Math.round(((product.comparePrice - product.price) / product.comparePrice) * 100)}% off
                      </span>
                    </>
                  )}
                </div>

                {product.stock > 0 ? (
                  <span className="stock-status in-stock">In Stock</span>
                ) : (
                  <span className="stock-status out-of-stock">Out of Stock</span>
                )}

                <div className="wishlist-item-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleAddToCart(product)}
                    disabled={product.stock === 0}
                  >
                    <FiShoppingCart /> Add to Cart
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => handleRemove(product._id)}
                  >
                    <FiTrash2 /> Remove
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Wishlist;
